import {ButtonGroup, closeCurrentDialog} from 'platform/components';
import {Center, HStack, Image, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {PricingStrategy} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {RadioCard} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import fairPriceIllustration from '../../../assets/pricing-strategy-fair-price.webp';
import lfppIllustration from '../../../assets/pricing-strategy-lfpp.webp';
import {PRICING_STRATEGY_OPTIONS} from '../../../constants/pricingStrategyOptions';

const PRICING_STRATEGY_CONTENT: Record<
  PricingStrategy,
  {titleKey: string; descriptionKey: string; illustration: string}
> = {
  [PricingStrategy.FAIR_PRICE]: {
    titleKey: 'page.sourcing.labels.pricingStrategy.fairPrice',
    descriptionKey: 'page.sourcing.labels.pricingStrategy.fairPriceDescription',
    illustration: fairPriceIllustration,
  },
  [PricingStrategy.LFPP]: {
    titleKey: 'page.sourcing.labels.pricingStrategy.lfpp',
    descriptionKey: 'page.sourcing.labels.pricingStrategy.lfppDescription',
    illustration: lfppIllustration,
  },
};

export interface SetPricingStrategyDialogProps extends TestIdProps {
  initialStrategy: PricingStrategy;
  onApply: (strategy: PricingStrategy) => void;
  /** Strategies offered in the dialog. Defaults to all strategies. */
  options?: PricingStrategy[];
}

export function SetPricingStrategyDialog(props: SetPricingStrategyDialogProps) {
  const options = props.options ?? PRICING_STRATEGY_OPTIONS;
  const [selectedStrategy, setSelectedStrategy] = useState(props.initialStrategy);

  const handleApply = () => {
    props.onApply(selectedStrategy);
    closeCurrentDialog();
  };

  return (
    <VStack spacing={4}>
      <HStack spacing={4} align="stretch" role="radiogroup">
        {options.map((strategy) => {
          const content = PRICING_STRATEGY_CONTENT[strategy];

          return (
            <RadioCard
              key={strategy}
              name="pricingStrategy"
              isSelected={selectedStrategy === strategy}
              title={i18n.t(content.titleKey)}
              description={i18n.t(content.descriptionKey)}
              onClick={() => setSelectedStrategy(strategy)}
              data-testid={suffixTestId(`option-${strategy}`, props)}
              illustration={
                <Center>
                  <Image
                    src={content.illustration}
                    alt={i18n.t(content.titleKey)}
                    width={40}
                    height={40}
                    fit="contain"
                  />
                </Center>
              }
            />
          );
        })}
      </HStack>
      <HStack justify="space-between" align="center" spacing={4}>
        <Text size="small" color="tertiary">
          {i18n.t('page.sourcing.labels.pricingStrategy.calculatedAfterConfirmation')}
        </Text>
        <ButtonGroup
          align="right"
          buttons={[
            {
              title: i18n.t('general.actions.cancel'),
              onClick: closeCurrentDialog,
              variant: 'secondary',
              'data-testid': suffixTestId('cancel', props),
            },
            {
              title: i18n.t('general.actions.apply'),
              onClick: handleApply,
              'data-testid': suffixTestId('apply', props),
            },
          ]}
        />
      </HStack>
    </VStack>
  );
}
